const MTSTAT_GUARD_FLAG = '__roboneoSafeGuarded';
const MTSTAT_RUNTIME_GUARD_FLAG = '__roboneoRuntimeGuardInstalled';

const MTSTAT_METHODS = [
  'track',
  'pageview',
  'setCommonParams',
  'setGid',
  'login',
  'logout',
  'init',
] as const;

/**
 * 判断是否为 mtstat-sdk 内部抛出的上报错误（如 F08）。
 */
function isMtstatSdkRuntimeError(
  error: unknown,
  source?: string | null,
): boolean {
  const message = error instanceof Error
    ? error.message
    : String(error ?? '');
  const stack = error instanceof Error ? error.stack ?? '' : '';

  return (
    /^F\d{2}$/.test(message)
    || Boolean(source?.includes('mtstat-sdk'))
    || stack.includes('mtstat-sdk')
  );
}

/**
 * 生成需在 mtstat-sdk 脚本之前注入的内联守卫脚本（仅开发环境 document 使用）。
 */
export function getMtstatRuntimeErrorGuardInlineScript(): string {
  return `(function(){if(window.${MTSTAT_RUNTIME_GUARD_FLAG})return;window.${MTSTAT_RUNTIME_GUARD_FLAG}=true;function i(e,r,s){var m=e&&e.message?e.message:String(e||"");var t=e&&e.stack?e.stack:"";return(/^F\\d{2}$/.test(m))||(r&&r.indexOf("mtstat-sdk")!==-1)||(t&&t.indexOf("mtstat-sdk")!==-1)||(s&&s.indexOf("mtstat-sdk")!==-1);}function h(e){if(i(e.error||e.message,e.filename,e.message)){e.preventDefault();e.stopImmediatePropagation();console.warn("[mtstat] suppressed runtime error:",e.message);}}function p(e){var r=e.reason;if(i(r,"",r&&r.stack?r.stack:"")){e.preventDefault();e.stopImmediatePropagation();console.warn("[mtstat] suppressed unhandled rejection:",r);}}window.addEventListener("error",h,true);window.addEventListener("unhandledrejection",p,true);})();`;
}

/**
 * 拦截 mtstat-sdk 异步上报失败，避免开发环境出现 Unhandled Runtime Error 遮罩。
 */
export function installMtstatRuntimeErrorGuard(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (process.env.NEXT_PUBLIC_ENV !== 'development') {
    return;
  }
  if ((window as Window & { [MTSTAT_RUNTIME_GUARD_FLAG]?: boolean })[MTSTAT_RUNTIME_GUARD_FLAG]) {
    return;
  }

  window.addEventListener('error', (event) => {
    if (isMtstatSdkRuntimeError(event.error ?? event.message, event.filename)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      console.warn('[mtstat] suppressed runtime error:', event.message);
    }
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    if (isMtstatSdkRuntimeError(event.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      console.warn('[mtstat] suppressed unhandled rejection:', event.reason);
    }
  }, true);

  (window as Window & { [MTSTAT_RUNTIME_GUARD_FLAG]?: boolean })[MTSTAT_RUNTIME_GUARD_FLAG] = true;
}

/**
 * 为 window.mtstat 的公开方法包一层 try-catch，避免同步调用失败影响页面。
 */
export function installMtstatMethodSafeGuard(): void {
  if (typeof window === 'undefined' || !window.mtstat) {
    return;
  }

  const mtstat = window.mtstat as typeof window.mtstat & { [MTSTAT_GUARD_FLAG]?: boolean };
  if (mtstat[MTSTAT_GUARD_FLAG]) {
    return;
  }

  MTSTAT_METHODS.forEach((method) => {
    const original = mtstat[method];
    if (typeof original !== 'function') {
      return;
    }

    mtstat[method] = ((...args: unknown[]) => {
      try {
        return original.apply(mtstat, args);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[mtstat] ${method} failed:`, error);
        }
        return undefined;
      }
    }) as typeof original;
  });

  mtstat[MTSTAT_GUARD_FLAG] = true;
}

/**
 * 安装 mtstat 运行时与方法双层防护。
 */
export function installMtstatSafeGuard(): void {
  installMtstatRuntimeErrorGuard();
  installMtstatMethodSafeGuard();
}
